import { useTranslation } from 'react-i18next';
import type { TransportTypeCategory } from '@magimp/shared';
import { Input, Select, Textarea } from '../../../ui/index.js';
import type { DraftForm } from './transport-type-form-model.js';

export function TransportTypeForm({
  form,
  onChange,
  mode,
  error,
  readOnly = false,
}: {
  form: DraftForm;
  onChange: (next: DraftForm) => void;
  mode: 'create' | 'edit';
  error: string | null;
  readOnly?: boolean;
}) {
  const { t } = useTranslation('transportTypes');
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      <Labeled label={t('fields.code')} required hint={t('fields.codeHint')}>
        <Input
          value={form.code}
          onChange={(code) => onChange({ ...form, code })}
          readOnly={mode === 'edit' || readOnly}
          monospace
        />
      </Labeled>
      <Labeled label={t('fields.category')} required>
        <Select
          value={form.category}
          onChange={(v) => onChange({ ...form, category: v as TransportTypeCategory })}
          options={[
            { value: 'truck', label: t('category.truck') },
            { value: 'container', label: t('category.container') },
          ]}
          disabled={readOnly}
        />
      </Labeled>
      <Labeled label={t('fields.nameRu')} required>
        <Input
          value={form.nameRu}
          onChange={(nameRu) => onChange({ ...form, nameRu })}
          readOnly={readOnly}
        />
      </Labeled>
      <Labeled label={t('fields.nameEn')} required>
        <Input
          value={form.nameEn}
          onChange={(nameEn) => onChange({ ...form, nameEn })}
          readOnly={readOnly}
        />
      </Labeled>
      <Labeled label={t('fields.volumeMin')} required>
        <Input
          value={form.volumeMin}
          onChange={(volumeMin) => onChange({ ...form, volumeMin })}
          readOnly={readOnly}
        />
      </Labeled>
      <Labeled label={t('fields.volumeMax')} required>
        <Input
          value={form.volumeMax}
          onChange={(volumeMax) => onChange({ ...form, volumeMax })}
          readOnly={readOnly}
        />
      </Labeled>
      <Labeled label={t('fields.payloadMin')} required>
        <Input
          value={form.payloadMin}
          onChange={(payloadMin) => onChange({ ...form, payloadMin })}
          readOnly={readOnly}
        />
      </Labeled>
      <Labeled label={t('fields.payloadMax')} required>
        <Input
          value={form.payloadMax}
          onChange={(payloadMax) => onChange({ ...form, payloadMax })}
          readOnly={readOnly}
        />
      </Labeled>
      <Labeled label={t('fields.sortOrder')}>
        <Input
          value={form.sortOrder}
          onChange={(sortOrder) => onChange({ ...form, sortOrder })}
          readOnly={readOnly}
        />
      </Labeled>
      <div />
      <div style={{ gridColumn: '1 / -1' }}>
        <Labeled label={t('fields.description')} required>
          <Textarea
            value={form.description}
            onChange={(description) => onChange({ ...form, description })}
            readOnly={readOnly}
          />
        </Labeled>
      </div>
      {error && (
        <div style={{ gridColumn: '1 / -1', color: 'var(--status-error-fg)', fontSize: 12 }}>
          {error}
        </div>
      )}
    </div>
  );
}

function Labeled({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12 }}>
      <span style={{ color: 'var(--fg-3)' }}>
        {label}
        {required ? ' *' : ''}
      </span>
      {children}
      {hint && <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>{hint}</span>}
    </label>
  );
}
